import React, { useEffect, useRef, useState } from "react";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { FlattenedError, focusField } from "../fieldLabelMap";
import "./FloatingFieldNavigator.css";

interface FloatingFieldNavigatorProps {
  errors: FlattenedError[];
  /** Formulario dueño de estos errores: limita la búsqueda del campo a esta pestaña. */
  scopeRef: React.RefObject<HTMLElement>;
}

const FloatingFieldNavigator: React.FC<FloatingFieldNavigatorProps> = ({ errors, scopeRef }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // La lista se recalcula en cada render, así que se compara por contenido: mirar sólo la cantidad
  // deja el recorrido corrido cuando se completa un campo y aparece otro faltante.
  const errorsKey = errors.map((error) => error.name).join("|");
  const previousErrorsKeyRef = useRef(errorsKey);

  useEffect(() => {
    if (previousErrorsKeyRef.current !== errorsKey) {
      previousErrorsKeyRef.current = errorsKey;
      setCurrentIndex(0);
    }
  }, [errorsKey]);

  if (errors.length === 0) return null;

  const handleNavigate = () => {
    // Un error puede quedar apuntando a un campo que ya no está en pantalla —la tarjeta que lo
    // contenía se cerró después del submit—: en ese caso se sigue con el próximo faltante que sí se
    // pueda mostrar, en lugar de dejar el botón sin efecto.
    for (let attempt = 0; attempt < errors.length; attempt += 1) {
      const index = (currentIndex + attempt) % errors.length;
      const error = errors[index];

      if (error && focusField(error.name, scopeRef.current)) {
        setCurrentIndex((index + 1) % errors.length);
        return;
      }
    }
  };

  const displayIndex = currentIndex + 1;

  return (
    <button
      type="button"
      className="floatingFieldNavigatorButton"
      onClick={handleNavigate}
      title="Ir al siguiente campo faltante"
    >
      <ErrorOutlineIcon className="floatingFieldNavigatorIcon" />
      <span className="floatingFieldNavigatorText">
        Ver faltante {displayIndex}/{errors.length}
      </span>
      <NavigateNextIcon className="floatingFieldNavigatorArrow" />
    </button>
  );
};

export default FloatingFieldNavigator;
